export type PageType = 'home' | 'blog' | 'blog-archive' | 'blog-single' | 'about' | 'contact' | 'landing' | 'pricing' | 'product' | 'login' | 'signup' | 'dashboard' | 'profile' | 'settings' | 'portfolio' | 'services' | 'careers' | 'faq' | 'terms' | 'privacy' | '404' | 'default';

export interface Section {
  id: string;
  name: string;
  description: string;
  copy: string;
  color?: string;
}

export interface PageMeta {
  metaTitle: string;
  metaDescription: string;
  jsonLd: string;
  featuredImage: string;
  featuredImageAlt: string;
}

export interface PageNode {
  id: string;
  name: string;
  type: PageType;
  color: string;
  x: number;
  y: number;
  sections: Section[];
  parentId?: string;
  meta?: PageMeta;
}

export type ConnectionSide = 'top' | 'bottom' | 'left' | 'right';

export interface Connection {
  id: string;
  fromId: string;
  toId: string;
  fromSide: ConnectionSide;
  toSide: ConnectionSide;
  style: 'straight' | 'curved' | 'step';
}

export interface ComponentTemplate {
  name: string;
  category: 'navigation' | 'hero' | 'content' | 'social-proof' | 'conversion' | 'footer' | 'utility';
}

export const COMPONENT_LIBRARY: ComponentTemplate[] = [
  // Navigation
  { name: 'Header', category: 'navigation' },
  { name: 'Navbar', category: 'navigation' },
  { name: 'Breadcrumbs', category: 'navigation' },
  { name: 'Sidebar Nav', category: 'navigation' },
  { name: 'Tab Navigation', category: 'navigation' },
  
  // Hero
  { name: 'Hero', category: 'hero' },
  { name: 'Hero with Video', category: 'hero' },
  { name: 'Hero with Image', category: 'hero' },
  { name: 'Split Hero', category: 'hero' },
  
  // Content
  { name: 'Features Grid', category: 'content' },
  { name: 'Features List', category: 'content' },
  { name: 'Content Block', category: 'content' },
  { name: 'Blog Grid', category: 'content' },
  { name: 'Blog List', category: 'content' },
  { name: 'Team Section', category: 'content' },
  { name: 'Gallery', category: 'content' },
  { name: 'Timeline', category: 'content' },
  { name: 'Stats', category: 'content' },
  { name: 'Comparison Table', category: 'content' },
  
  // Social Proof
  { name: 'Testimonials', category: 'social-proof' },
  { name: 'Logo Cloud', category: 'social-proof' },
  { name: 'Reviews', category: 'social-proof' },
  { name: 'Case Studies', category: 'social-proof' },
  
  // Conversion
  { name: 'CTA Section', category: 'conversion' },
  { name: 'Pricing Table', category: 'conversion' },
  { name: 'Newsletter Signup', category: 'conversion' },
  { name: 'Contact Form', category: 'conversion' },
  { name: 'Lead Capture', category: 'conversion' },
  
  // Footer
  { name: 'Footer', category: 'footer' },
  { name: 'Footer Minimal', category: 'footer' },
  { name: 'Footer with CTA', category: 'footer' },
  
  // Utility
  { name: 'FAQ', category: 'utility' },
  { name: 'Map', category: 'utility' },
  { name: 'Divider', category: 'utility' },
  { name: 'Banner', category: 'utility' },
];

export const PAGE_COLORS = [
  '#2DD4A8', // teal
  '#8B5CF6', // violet
  '#F59E0B', // amber
  '#3B82F6', // blue
  '#EF4444', // red
  '#EC4899', // pink
  '#10B981', // emerald
  '#6366F1', // indigo
  '#F97316', // orange
  '#64748B', // slate
  // Light shades
  '#6EE7B7', // teal light
  '#C4B5FD', // violet light
  '#FCD34D', // amber light
  '#93C5FD', // blue light
  '#FCA5A5', // red light
  '#F9A8D4', // pink light
  '#6EE7B7', // emerald light
  '#A5B4FC', // indigo light
  '#FDBA74', // orange light
];

export const DEFAULT_PAGE_COLORS: Record<PageType, string> = {
  home: '#2DD4A8',
  blog: '#8B5CF6',
  'blog-archive': '#8B5CF6',
  'blog-single': '#A78BFA',
  about: '#F59E0B',
  contact: '#3B82F6',
  landing: '#EC4899',
  pricing: '#6366F1',
  product: '#F97316',
  login: '#64748B',
  signup: '#64748B',
  dashboard: '#10B981',
  profile: '#3B82F6',
  settings: '#64748B',
  portfolio: '#EC4899',
  services: '#F59E0B',
  careers: '#EF4444',
  faq: '#6366F1',
  terms: '#64748B',
  privacy: '#64748B',
  '404': '#EF4444',
  default: '#64748B',
};
